import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Previsão do próximo envio de um envio recorrente, espelhando o backend:
 * - a lambda createDispatchMessage (00:00 de Brasília) cria os disparos de cada dia
 *   em que DATE(startAt) <= dia <= DATE(endAt), no horário diário ou do dia da semana;
 * - ao salvar, o backend cria o disparo de hoje só se o horário ainda não passou.
 * Datas e horários contam em Brasília: o backend grava o horário convertido para lá.
 */

const TIMEZONE = 'America/Sao_Paulo';
const WEEKDAYS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const NEAR_DEADLINE_MINUTES = 10;

type DaySchedule = {
  dayOfTheWeek: number;
  startTime?: string | Date | null;
  isActive?: boolean;
};

export type RecurringSendScheduleValues = {
  isActive?: boolean;
  startAt?: string | Date | null;
  endAt?: string | Date | null;
  frequency?: string;
  startTime?: string | Date | null;
  weekSchedule?: DaySchedule[] | null;
  // Edição: o registro vem da API com a relação, não com weekSchedule
  dayScheduledRecurringSends?: DaySchedule[] | null;
};

export type NextSend =
  | { kind: 'incomplete' }
  | { kind: 'paused' }
  | { kind: 'none' }
  | { kind: 'scheduled'; sendAt: Date; todayPassed: boolean };

export type ScheduleNotice = {
  severity: 'info' | 'warning' | 'error';
  message: string;
};

// TimeInput manda Date no fuso do navegador; o registro salvo traz "HH:mm" já em Brasília
const toSaoPauloTime = (value?: string | Date | null): string | null => {
  if (!value) { return null; }
  if (typeof value === 'string' && /^\d{2}:\d{2}/.test(value)) { return value.slice(0, 5); }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(TIMEZONE).format('HH:mm') : null;
};

const toSaoPauloDate = (value?: string | Date | null): string | null => {
  if (!value) { return null; }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(TIMEZONE).format('YYYY-MM-DD') : null;
};

// Na edição o ArrayInput pode registrar weekSchedule como [] — aí vale a relação do registro
const getDaySchedules = (values: RecurringSendScheduleValues): DaySchedule[] =>
  values.weekSchedule?.length ? values.weekSchedule : (values.dayScheduledRecurringSends ?? []);

const getTimeForWeekday = (values: RecurringSendScheduleValues, weekday: number): string | null => {
  if (values.frequency === 'daily') {
    return toSaoPauloTime(values.startTime);
  }
  const schedule = getDaySchedules(values).find(ds => ds.isActive && Number(ds.dayOfTheWeek) === weekday);
  return toSaoPauloTime(schedule?.startTime);
};

const hasAnySchedule = (values: RecurringSendScheduleValues): boolean => {
  if (values.frequency === 'daily') { return !!toSaoPauloTime(values.startTime); }
  if (values.frequency === 'weekly') {
    return getDaySchedules(values).some(ds => ds.isActive && !!toSaoPauloTime(ds.startTime));
  }
  return false;
};

/** Primeiro envio com horário estritamente depois de `after`. */
export const getNextSend = (values: RecurringSendScheduleValues, after: Date = new Date()): NextSend => {
  if (!values) { return { kind: 'incomplete' }; }
  if (values.isActive === false) { return { kind: 'paused' }; }

  const startDate = toSaoPauloDate(values.startAt);
  if (!startDate || !hasAnySchedule(values)) { return { kind: 'incomplete' }; }

  const endDate = toSaoPauloDate(values.endAt);
  const afterDate = dayjs(after).tz(TIMEZONE).format('YYYY-MM-DD');
  const firstDate = startDate > afterDate ? startDate : afterDate;
  let todayPassed = false;

  // 8 dias cobre o semanal cujo único dia é hoje e o horário já passou
  for (let offset = 0; offset <= 7; offset++) {
    const day = dayjs(firstDate).add(offset, 'day');
    const date = day.format('YYYY-MM-DD');
    if (endDate && date > endDate) { break; }

    const time = getTimeForWeekday(values, day.day());
    if (!time) { continue; }

    const sendAt = dayjs.tz(`${date}T${time}:00`, TIMEZONE);
    if (sendAt.valueOf() > after.getTime()) {
      return { kind: 'scheduled', sendAt: sendAt.toDate(), todayPassed };
    }
    todayPassed = true;
  }

  return { kind: 'none' };
};

/** "hoje (16/09) às 18:00", "amanhã (17/09) às 18:00", "sexta (18/09) às 09:00", "30/09 às 18:00" */
export const describeSendAt = (sendAt: Date, now: Date = new Date()): string => {
  const target = dayjs(sendAt).tz(TIMEZONE);
  const targetDay = dayjs(target.format('YYYY-MM-DD'));
  const diffDays = targetDay.diff(dayjs(toSaoPauloDate(now)), 'day');
  const when = target.format('DD/MM');
  const time = target.format('HH:mm');

  // Só vale citar o fuso quando o navegador não está no horário de Brasília
  const timezoneSuffix = dayjs(now).utcOffset() !== dayjs(now).tz(TIMEZONE).utcOffset() ? ' (horário de Brasília)' : '';

  if (diffDays === 0) { return `hoje (${when}) às ${time}${timezoneSuffix}`; }
  if (diffDays === 1) { return `amanhã (${when}) às ${time}${timezoneSuffix}`; }
  if (diffDays > 1 && diffDays < 7) { return `${WEEKDAYS[targetDay.day()]} (${when}) às ${time}${timezoneSuffix}`; }
  return `${when} às ${time}${timezoneSuffix}`;
};

/** Aviso exibido no formulário enquanto o usuário preenche. null = nada a mostrar ainda. */
export const buildScheduleNotice = (values: RecurringSendScheduleValues, now: Date = new Date()): ScheduleNotice | null => {
  const next = getNextSend(values, now);

  if (next.kind === 'incomplete') { return null; }
  if (next.kind === 'paused') {
    return { severity: 'info', message: 'Envio pausado: nenhum disparo será feito enquanto estiver pausado.' };
  }
  if (next.kind === 'none') {
    return { severity: 'error', message: 'Nenhum envio cabe no período: a data final termina antes do próximo horário de envio.' };
  }

  const label = describeSendAt(next.sendAt, now);
  const isToday = toSaoPauloDate(next.sendAt) === toSaoPauloDate(now);
  const minutesLeft = (next.sendAt.getTime() - now.getTime()) / 60000;

  if (isToday && minutesLeft <= NEAR_DEADLINE_MINUTES) {
    const after = getNextSend(values, next.sendAt);
    const fallback = after.kind === 'scheduled'
      ? `o próximo envio passa a ser ${describeSendAt(after.sendAt, now)}`
      : 'nenhum envio cabe mais no período';
    return {
      severity: 'warning',
      message: `Próximo envio: ${label}. Salve antes desse horário — depois disso, ${fallback}.`,
    };
  }

  if (next.todayPassed) {
    return { severity: 'warning', message: `O horário de hoje já passou. Próximo envio: ${label}.` };
  }

  return { severity: 'info', message: `Próximo envio: ${label}.` };
};

/**
 * Complemento da notificação depois de salvar, a partir do `todaySendAt` devolvido
 * pelo backend (fonte da verdade: ele sabe se hoje já houve disparo).
 * null = resposta sem a informação (sem alterações ou backend antigo) → usar a mensagem padrão.
 */
export const buildSavedMessage = (
  values: RecurringSendScheduleValues,
  todaySendAt: string | Date | null | undefined,
  now: Date = new Date(),
): string | null => {
  if (todaySendAt === undefined) { return null; }

  if (todaySendAt) {
    return `Próximo envio: ${describeSendAt(new Date(todaySendAt), now)}.`;
  }

  // Nada hoje: procura a partir do fim do dia de hoje em Brasília
  const endOfToday = dayjs(now).tz(TIMEZONE).endOf('day').toDate();
  const next = getNextSend(values, endOfToday);
  if (next.kind === 'scheduled') { return `Próximo envio: ${describeSendAt(next.sendAt, now)}.`; }
  if (next.kind === 'none') { return 'Nenhum envio cabe no período configurado.'; }
  return null;
};
